import shortid from "shortid";
import { Glue42 } from "../../glue";
import { AppDefinition, CoreRaiseIntentArgs, GetWindowsInfoResult, GetWindowsInfoResultItem, IntentInfo, IntentsResolverResponse, IntentsResolverResponsePromise, IntentsResolverResponseValidator, IntentsResolverStartContext, ResolverInstance, ShouldResolverOpen } from "./types";
import { INTENTS_RESOLVER_INTEROP_PREFIX, GLUE42_FDC3_INTENTS_METHOD_PREFIX, INTENTS_RESOLVER_HEIGHT, INTENTS_RESOLVER_WIDTH, INTENT_HANDLER_DEFAULT_PROPS, INTENTS_RESOLVER_APP_NAME, DEFAULT_RESOLVER_RESPONSE_TIMEOUT, DEFAULT_RAISE_TIMEOUT_MS, DEFAULT_METHOD_RESPONSE_TIMEOUT_MS, MAX_SET_TIMEOUT_DELAY } from "./constants";
import { PromisePlus, PromiseWrap } from "./common";

export class Intents implements Glue42.Intents.API {
    private myIntents = new Set<string>();

    private intentsResolverAppName: string;
    private readonly intentsResolverResponsePromises: { [instanceId: string]: IntentsResolverResponsePromise } = {};

    private useIntentsResolverUI = true;
    private intentsResolverResponseTimeout: number;

    private unregisterIntentPromises: Promise<void>[] = [];

    constructor(private interop: Glue42.Interop.API, private windows: Glue42.Windows.API, private logger: Glue42.Logger.API, options: Glue42.Config, private appManager?: Glue42.AppManager.API) {
        this.checkIfIntentsResolverIsEnabled(options, appManager);
    }

    public async find(intentFilter?: string | Glue42.Intents.IntentFilter): Promise<Glue42.Intents.Intent[]> {
        await Promise.all(this.unregisterIntentPromises);

        let intents = await this.all();
        if (typeof intentFilter === "undefined") {
            return intents;
        }

        if (typeof intentFilter === "string") {
            return intents.filter((intent) => intent.name === intentFilter);
        }

        if (typeof intentFilter !== "object") {
            throw new Error("Please provide the intentFilter as a string or an object!");
        }

        if (intentFilter.contextType) {
            const ctToLower = intentFilter.contextType.toLowerCase();
            intents = intents.filter((intent) => intent.handlers.some((handler) => handler.contextTypes?.some((ct) => ct.toLowerCase() === ctToLower)));
        }

        if (intentFilter.resultType) {
            const resultTypeToLower = intentFilter.resultType.toLowerCase();
            intents = intents.filter((intent) => intent.handlers.some((handler) => handler.resultType?.toLowerCase() === resultTypeToLower));
        }

        if (intentFilter.name) {
            intents = intents.filter((intent) => intent.name === intentFilter.name);
        }

        return intents;
    }

    public async raise(intentRequest: string | Glue42.Intents.IntentRequest): Promise<Glue42.Intents.IntentResult> {
        if ((typeof intentRequest !== "string" && typeof intentRequest !== "object") || (typeof intentRequest === "object" && typeof intentRequest.intent !== "string")) {
            throw new Error("Please provide the intent as a string or an object with an intent property!");
        }

        if (typeof intentRequest === "string") {
            intentRequest = { intent: intentRequest };
        }

        this.validateIntentRequest(intentRequest);

        await Promise.all(this.unregisterIntentPromises);

        const timeout = intentRequest.timeout || DEFAULT_RAISE_TIMEOUT_MS;

        const resolverInstance: ResolverInstance = {};

        const coreRaiseIntentFn = this.coreRaiseIntent.bind(this, { request: intentRequest, resolverInstance, timeout });

        if (intentRequest.waitUserResponseIndefinitely) {
            return coreRaiseIntentFn();
        }

        // start timeout here -> default (waitUserResponseIndefinitely: false)
        const resultPromise = PromiseWrap<Glue42.Intents.IntentResult>(coreRaiseIntentFn,
            timeout, `Timeout of ${timeout}ms hit for intent request ${JSON.stringify(intentRequest)}`
        );

        resultPromise.catch(() => this.handleRaiseOnError(resolverInstance.instanceId));

        return resultPromise;
    }

    public async all(): Promise<Glue42.Intents.Intent[]> {
        await Promise.all(this.unregisterIntentPromises);

        // Gathers all intents from:
        // 1. Application definitions
        // 2. Running instances (application can register dynamic intents by calling `addIntentListener()` that aren't predefined inside of their application definitions)
        // It also populates intent handlers (actual entities that can handle the intent).
        let apps: AppDefinition[];
        try {
            const result = await this.interop.invoke<{ applications: AppDefinition[] }>("T42.ACS.GetApplications", { withIntentsInfo: true });
            apps = result.returned.applications;
        } catch (e) {
            this.logger.error(`Failed to get the applications!`, e);
            return [];
        }
        const intents: { [key: string]: Glue42.Intents.Intent } = {};
        const appsWithIntents = apps.filter((app) => app.intents && app.intents.length > 0);
        //  Gather app handlers from application definitions.
        for (const app of appsWithIntents) {
            for (const intentDef of app.intents) {
                let intent = intents[intentDef.name];
                if (!intent) {
                    intent = {
                        name: intentDef.name,
                        handlers: [],
                    };
                    intents[intentDef.name] = intent;
                }

                const handler: Glue42.Intents.IntentHandler = {
                    applicationName: app.name,
                    applicationTitle: app.title || "",
                    applicationDescription: app.caption,
                    displayName: intentDef.displayName,
                    contextTypes: intentDef.contexts,
                    applicationIcon: app.icon,
                    type: "app",
                    resultType: intentDef.resultType
                };

                intent.handlers.push(handler);
            }
        }

        const servers = this.interop.servers();
        const serverWindowIds = servers.map((server) => server.windowId).filter((serverWindowId) => typeof serverWindowId !== "undefined");

        const T42WndGetInfo = "T42.Wnd.GetInfo";
        const isT42WndGetInfoMethodRegistered = this.interop.methods().some((method) => method.name === T42WndGetInfo);

        let windowsInfos: GetWindowsInfoResultItem[];
        // For backwards compatibility with previous versions of Glue Desktop, the windows' titles will be attempted to be obtained using the Windows API.
        if (isT42WndGetInfoMethodRegistered) {
            try {
                const result = await this.interop.invoke<GetWindowsInfoResult>(T42WndGetInfo, { ids: serverWindowIds });
                windowsInfos = result.returned.windows;
            } catch (e) {
                // Swallow the error. The windows' titles will be attempted to be obtained using the Windows API.
            }
        }

        // Discover all running instances that provide intents, and add them to the corresponding intent.
        for (const server of servers) {
            await Promise.all(server.getMethods()
                .filter((method) => method.name.startsWith(GLUE42_FDC3_INTENTS_METHOD_PREFIX))
                .map(async (method) => {
                    const intentName = method.name.replace(GLUE42_FDC3_INTENTS_METHOD_PREFIX, "");
                    let intent = intents[intentName];
                    if (!intent) {
                        intent = {
                            name: intentName,
                            handlers: [],
                        };
                        intents[intentName] = intent;
                    }
                    const info = method.flags.intent as Omit<Glue42.Intents.AddIntentListenerRequest, "intent">;

                    const app = apps.find((appWithIntents) => appWithIntents.name === server.application);
                    let appIntent: IntentInfo | undefined;
                    if (app && app.intents) {
                        appIntent = app.intents.find((appDefIntent) => appDefIntent.name === intentName);
                    }

                    const title = await this.windowsIdToTitle(server.windowId, windowsInfos);
                    const handler: Glue42.Intents.IntentHandler = {
                        instanceId: server.instance,
                        applicationName: server.application,
                        applicationIcon: info.icon || app?.icon,
                        applicationTitle: app?.title || "",
                        applicationDescription: info.description || app?.caption,
                        displayName: info.displayName || appIntent?.displayName,
                        contextTypes: info.contextTypes || appIntent?.contexts,
                        instanceTitle: title,
                        type: "instance",
                        resultType: appIntent?.resultType || info.resultType
                    };
                    intent.handlers.push(handler);
                }));
        }

        return Object.values(intents);
    }

    public addIntentListener(intent: string | Glue42.Intents.AddIntentListenerRequest, handler: (context: Glue42.Intents.IntentContext) => any): { unsubscribe: () => void } {
        if ((typeof intent !== "string" && typeof intent !== "object") || (typeof intent === "object" && typeof intent.intent !== "string")) {
            throw new Error("Please provide the intent as a string or an object with an intent property!");
        }
        if (typeof handler !== "function") {
            throw new Error("Please provide the handler as a function!");
        }

        // `addIntentListener()` is sync.
        // tslint:disable-next-line:no-console
        const intentName = typeof intent === "string" ? intent : intent.intent;
        const methodName = `${GLUE42_FDC3_INTENTS_METHOD_PREFIX}${intentName}`;
        let intentFlag: Omit<Glue42.Intents.AddIntentListenerRequest, "intent"> = {};
        // eslint-disable-next-line prefer-const
        let registerPromise: Promise<void>;

        const alreadyRegistered = this.myIntents.has(intentName);

        if (alreadyRegistered) {
            throw new Error(`Intent listener for intent ${intentName} already registered!`);
        }

        this.myIntents.add(intentName);

        const result = {
            unsubscribe: (): void => {
                this.myIntents.delete(intentName);

                registerPromise
                    .then(() => this.interop.unregister(methodName))
                    .catch((err) => this.logger.trace(`Unregistration of a method with name ${methodName} failed with reason: ${JSON.stringify(err)}`));
            }
        };

        if (typeof intent === "object") {
            const { intent: removed, ...rest } = intent;
            intentFlag = rest;
        }

        registerPromise = this.interop.register({ name: methodName, flags: { intent: intentFlag } }, (args: Glue42.Intents.IntentContext, caller: Glue42.Interop.Instance) => {
            if (this.myIntents.has(intentName)) {
                return handler(args);
            }
        });

        registerPromise.catch((err) => {
            this.myIntents.delete(intentName);

            this.logger.warn(`Registration of a method with name ${methodName} failed with reason: ${JSON.stringify(err)}`);
        });

        return result;
    }

    public async register(intent: string | Glue42.Intents.AddIntentListenerRequest, handler: (context: Glue42.Intents.IntentContext, caller?: Glue42.Interop.Instance) => any): Promise<{ unsubscribe: () => void }> {
        if ((typeof intent !== "string" && typeof intent !== "object") || (typeof intent === "object" && typeof intent.intent !== "string")) {
            throw new Error("Please provide the intent as a string or an object with an intent property!");
        }

        if (typeof handler !== "function") {
            throw new Error("Please provide the handler as a function!");
        }

        await Promise.all(this.unregisterIntentPromises);

        // tslint:disable-next-line:no-console
        const intentName = typeof intent === "string" ? intent : intent.intent;
        const methodName = this.buildInteropMethodName(intentName);

        let intentFlag: Omit<Glue42.Intents.AddIntentListenerRequest, "intent"> = {};

        const alreadyRegistered = this.myIntents.has(intentName);

        if (alreadyRegistered) {
            throw new Error(`Intent listener for intent ${intentName} already registered!`);
        }

        this.myIntents.add(intentName);

        if (typeof intent === "object") {
            const { intent: removed, ...rest } = intent;
            intentFlag = rest;
        }

        try {
            await this.interop.register({ name: methodName, flags: { intent: intentFlag } }, (args: Glue42.Intents.IntentContext, caller?: Glue42.Interop.Instance) => {
                if (this.myIntents.has(intentName)) {
                    return handler(args, caller);
                }
            });
        } catch (err) {
            this.myIntents.delete(intentName);

            throw new Error(`Registration of a method with name ${methodName} failed with reason: ${JSON.stringify(err)}`);
        }

        return {
            unsubscribe: () => this.unsubscribeIntent(intentName)
        };
    }

    private async coreRaiseIntent({ request, resolverInstance, timeout }: CoreRaiseIntentArgs): Promise<Glue42.Intents.IntentResult> {
        const intentDef = await this.get(request.intent);

        if (typeof intentDef === "undefined") {
            throw new Error(`Intent ${request.intent} not found.`);
        }

        const { open, reason } = this.checkIfResolverShouldBeOpened(intentDef, request);

        if (!open) {
            this.logger.trace(`Intent Resolver UI won't be used. Reason: ${reason}`);

            // start timeout here -> waitUserResponseIndefinitely: true, but resolver will not be opened
            return request.waitUserResponseIndefinitely
                ? PromiseWrap<Glue42.Intents.IntentResult>(() => this.raiseIntent(request, timeout), timeout, `Timeout of ${timeout}ms hit for raise to resolve`)
                : this.raiseIntent(request, timeout);
        }

        const resolverHandler = await this.startResolverApp(request, resolverInstance);

        this.logger?.trace(`Raising intent to target handler: ${JSON.stringify(resolverHandler)} with timeout of ${timeout}`);

        if (request.waitUserResponseIndefinitely) {
            return PromiseWrap(() => this.raiseIntentToTargetHandler(request, resolverHandler, timeout), timeout, `Timeout of ${timeout}ms hit for raise to resolve`);
        }

        const result = await this.raiseIntentToTargetHandler(request, resolverHandler, timeout);

        this.logger?.trace(`Result from raise() method for intent ${JSON.stringify(request.intent)}: ${JSON.stringify(result)}`);

        return result;
    }

    private async get(intent: string): Promise<Glue42.Intents.Intent> {
        return (await this.all()).find((registeredIntent) => registeredIntent.name === intent);
    }

    private async raiseIntent(intentRequest: Glue42.Intents.IntentRequest, timeout?: number): Promise<Glue42.Intents.IntentResult> {
        const intentName = intentRequest.intent;
        const intentDef = await this.get(intentName);

        if (typeof intentDef === "undefined") {
            throw new Error(`Intent ${intentName} not found.`);
        }

        const firstFoundAppHandler = intentRequest.handlers ? this.findHandlerByFilter(intentRequest.handlers, { type: "app" }) : this.findHandlerByFilter(intentDef.handlers, { type: "app" });

        const firstFoundInstanceHandler = intentRequest.handlers ? this.findHandlerByFilter(intentRequest.handlers, { type: "instance" }) : this.findHandlerByFilter(intentDef.handlers, { type: "instance" });

        let handler: Glue42.Intents.IntentHandler | undefined;

        // target the first found instance or start a new one if there isn't
        if (!intentRequest.target || intentRequest.target === "reuse") {
            handler = firstFoundInstanceHandler || firstFoundAppHandler;
        }

        // start new instance of the first handler in handlers / intentDef.handlers
        if (intentRequest.target === "startNew") {
            handler = firstFoundAppHandler;
        }

        // open a new app from the list of intentRequest.handlers (if passed) or intentDef.handlers
        if (typeof intentRequest.target === "object" && intentRequest.target.app) {
            handler = this.findHandlerByFilter(intentDef.handlers, { app: intentRequest.target.app });
        }

        // target the instance in the list of intentRequest.handlers (if passed) or intentDef.handlers
        if (typeof intentRequest.target === "object" && intentRequest.target.instance) {
            handler = this.findHandlerByFilter(intentDef.handlers, { instance: intentRequest.target.instance, app: intentRequest.target.app });
        }

        if (!handler) {
            throw new Error(`Can not raise intent for request ${JSON.stringify(intentRequest)} - can not find intent handler!`);
        }

        const result = await this.raiseIntentToTargetHandler(intentRequest, handler, timeout);

        return result;
    }

    private async raiseIntentToTargetHandler(intentRequest: Glue42.Intents.IntentRequest, handler: Glue42.Intents.IntentHandler, timeout?: number): Promise<Glue42.Intents.IntentResult> {
        this.logger?.trace(`Raising intent to target handler:${JSON.stringify(handler)}`);

        if (!handler.instanceId) {
            handler.instanceId = await this.invokeStartApp(handler.applicationName, intentRequest.context, intentRequest.options);
        }

        const methodName = `${GLUE42_FDC3_INTENTS_METHOD_PREFIX}${intentRequest.intent}`;

        const invokeOptions = {
            methodResponseTimeoutMs: timeout ? timeout + 1000 : DEFAULT_METHOD_RESPONSE_TIMEOUT_MS,
            waitTimeoutMs: timeout ? timeout + 1000 : DEFAULT_METHOD_RESPONSE_TIMEOUT_MS
        };

        const result = await this.interop.invoke(methodName, intentRequest.context, { instance: handler.instanceId }, invokeOptions);

        this.logger?.trace(`raiseIntent command completed. Returning result: ${JSON.stringify(result)}`);

        return {
            request: intentRequest,
            handler: { ...handler, type: "instance" },
            result: result.returned
        };
    }

    private async startResolverApp(intentRequest: Glue42.Intents.IntentRequest, resolverInstance?: ResolverInstance): Promise<Glue42.Intents.IntentHandler> {
        this.logger?.trace(`Intents Resolver UI with app name ${this.intentsResolverAppName} will be used`);

        const responseMethodName = await this.registerIntentResolverMethod();

        this.logger?.trace(`Registered interop method ${responseMethodName}`);

        const startContext = this.buildStartContext(intentRequest, responseMethodName);

        const startOptions = await this.buildStartOptions();

        this.logger?.trace(`Starting Intents Resolver UI with context: ${JSON.stringify(startContext)} and options: ${JSON.stringify(startOptions)}`);

        const instance = await this.appManager.application(this.intentsResolverAppName).start(startContext, startOptions);

        if (resolverInstance) {
            resolverInstance.instanceId = instance.id;
        }

        this.logger?.trace(`Intents Resolver instance with id ${instance.id} opened`);

        this.subscribeOnInstanceStopped(instance);

        this.createResponsePromise(intentRequest, instance.id, responseMethodName);

        const handler = await this.handleInstanceResponse(instance.id);

        return handler;
    }

    private async windowsIdToTitle(id: string, windowsInfos?: GetWindowsInfoResultItem[]): Promise<string> {
        if (typeof windowsInfos !== "undefined") {
            return windowsInfos.find((windowsInfo) => windowsInfo.id === id)?.title;
        }

        // The Windows API could be undefined e.g. when running inside of a node environment.
        const window = this.windows?.findById(id);
        const title = await window?.getTitle();
        return title;
    }

    private async handleInstanceResponse(instanceId: string): Promise<Glue42.Intents.IntentHandler> {
        try {
            const { handler, intent } = await this.intentsResolverResponsePromises[instanceId].promise;

            this.logger?.trace(`Intent handler chosen for intent ${intent}: ${JSON.stringify(handler)}`);

            this.stopResolverInstance(instanceId);

            return handler;

        } catch (error) {
            this.stopResolverInstance(instanceId);

            throw new Error(error as string);
        }
    }

    private async registerIntentResolverMethod(): Promise<string> {
        const methodName = INTENTS_RESOLVER_INTEROP_PREFIX + shortid();

        await this.interop.register(methodName, this.resolverResponseHandler.bind(this));

        return methodName;
    }

    private resolverResponseHandler(args: any, callerId: Glue42.Interop.Instance): void {
        const { instance } = callerId;

        const isValid = this.validateIntentsResolverResponse(args);

        if (!isValid) {
            this.logger.trace(`Intent Resolver sent invalid response. Error: ${isValid.error}`);

            this.intentsResolverResponsePromises[instance].reject(isValid.error);

            this.stopResolverInstance(instance);
        }

        const validResponse = isValid.ok;

        this.intentsResolverResponsePromises[instance].resolve(validResponse);

        this.cleanUpIntentResolverPromise(instance);
    }

    private buildStartContext(request: Glue42.Intents.IntentRequest, methodName: string): IntentsResolverStartContext {
        const startContext: IntentsResolverStartContext = {
            intent: request,
            callerId: this.interop.instance.instance,
            methodName
        };

        return startContext;
    }

    private async buildStartOptions(): Promise<Glue42.AppManager.ApplicationStartOptions | undefined> {
        const win = this.windows.my();

        if (!win) {
            return;
        }

        const bounds = await win.getBounds();

        return {
            top: (bounds.height - INTENTS_RESOLVER_HEIGHT) / 2 + bounds.top,
            left: (bounds.width - INTENTS_RESOLVER_WIDTH) / 2 + bounds.left,
        };
    }

    private createResponsePromise(intentRequest: Glue42.Intents.IntentRequest, instanceId: string, methodName: string): void {
        let resolve;
        let reject;

        const waitResponseTimeout = intentRequest.waitUserResponseIndefinitely ? MAX_SET_TIMEOUT_DELAY : this.intentsResolverResponseTimeout;

        const promise = PromisePlus<IntentsResolverResponse>((res, rej) => {
            resolve = res;
            reject = rej;
        },
            waitResponseTimeout,
            `Timeout of ${waitResponseTimeout}ms hit waiting for the user to choose a handler for intent ${intentRequest.intent}`);

        this.intentsResolverResponsePromises[instanceId] = { intent: intentRequest.intent, resolve, reject, promise, methodName };
    }

    private async invokeStartApp(application: string, context: any, options?: Glue42.AppManager.ApplicationStartOptions) {
        const result = await this.interop.invoke<{ Name: string, Id: string }>("T42.ACS.StartApplication", { Name: application, options });
        return result.returned.Id;
    }

    private subscribeOnInstanceStopped(instance: Glue42.AppManager.Instance): void {
        const unsub = instance.onStopped((inst: Glue42.AppManager.Instance) => {
            const intentPromise = this.intentsResolverResponsePromises[inst.id];

            if (!intentPromise) {
                return unsub();
            }

            intentPromise.reject(`Cannot resolve raise intent ${intentPromise.intent} - User closed ${this.intentsResolverAppName} app without choosing an intent handler`);

            this.cleanUpIntentResolverPromise(inst.id);

            unsub();
        });
    }

    private async cleanUpIntentResolverPromise(instanceId: string) {
        const intentPromise = this.intentsResolverResponsePromises[instanceId];

        if (!intentPromise) {
            return;
        }

        // typings are wrong and mark unregister as a sync method
        const unregisterPromise = this.interop.unregister(intentPromise.methodName) as unknown as Promise<void>;

        unregisterPromise.catch((error) => this.logger.warn(error));

        delete this.intentsResolverResponsePromises[instanceId];
    }

    private validateIntentsResolverResponse(responseObj: any): IntentsResolverResponseValidator {
        const invalidKey = Object.keys(responseObj).some((key) => key !== "intent" && key !== "handler");

        if (invalidKey) {
            return { isValid: false, error: "Response is not a valid object. Expected { intent: string, handler: IntentHandler }" };
        }

        if (typeof responseObj.intent !== "string") {
            return { isValid: false, error: `Response object has invalid 'intent' key. Expected a string, got ${typeof responseObj.intent}` };
        }

        if (typeof responseObj.handler !== "object") {
            return { isValid: false, error: `Response object has invalid 'handler' key. Expected an object, got ${typeof responseObj.handler}` };
        }

        const compulsoryKeysExist = INTENT_HANDLER_DEFAULT_PROPS.filter((key) => !(key in responseObj.handler));

        if (compulsoryKeysExist.length) {
            return { isValid: false, error: `Handler in Response object does not provide compulsory keys: ${compulsoryKeysExist.join(", ")}` };
        }

        return { isValid: true, ok: { intent: responseObj.intent, handler: { ...responseObj.handler } } };
    }

    private handleRaiseOnError(instanceId?: string) {
        if (!instanceId) {
            return;
        }

        this.stopResolverInstance(instanceId);
    }

    private stopResolverInstance(instanceId: string): void {
        const gdWin = this.windows.findById(instanceId);

        gdWin?.close().catch((err) => this.logger.error(err));
    }

    private checkIfIntentsResolverIsEnabled(options: Glue42.Config, appManager: Glue42.AppManager.API): void {
        /* if Glue is initialized with { appManager: false }, fallback to previous implementation and do not use Intents Resolver UI */
        if (!appManager) {
            this.useIntentsResolverUI = false;
            return;
        }

        this.useIntentsResolverUI = typeof options.intents?.enableIntentsResolverUI === "boolean"
            ? options.intents.enableIntentsResolverUI
            : true;

        this.intentsResolverAppName = options.intents?.intentsResolverAppName ?? INTENTS_RESOLVER_APP_NAME;

        this.intentsResolverResponseTimeout = options.intents?.methodResponseTimeoutMs ?? DEFAULT_RESOLVER_RESPONSE_TIMEOUT;
    }

    private checkIfResolverShouldBeOpened(intent: Glue42.Intents.Intent, request: Glue42.Intents.IntentRequest): ShouldResolverOpen {
        if (!this.useIntentsResolverUI) {
            return { open: false, reason: `Intent Resolver is disabled. Raising intent to first found handler` };
        }

        const intentsResolverApp = this.appManager.application(this.intentsResolverAppName);

        if (!intentsResolverApp) {
            return { open: false, reason: `Intent Resolver Application with name ${this.intentsResolverAppName} not found.` };
        }

        const hasMoreThanOneHandler = this.checkIfIntentHasMoreThanOneHandler(intent, request);

        if (!hasMoreThanOneHandler) {
            return { open: false, reason: `Raised intent ${intent.name} has only one handler` };
        }

        return { open: true };
    }

    private checkIfIntentHasMoreThanOneHandler(intent: Glue42.Intents.Intent, request: Glue42.Intents.IntentRequest): boolean {
        // no target => show resolver if request.handlers > 1 || intent.handlers.length > 1
        // reuse => show resolver if 2+ instances in request.handlers || show resolver if intent.handlers.length > 1
        // startNew => show resolver if 2+ apps in request.handler || intent.handlers as apps > 1
        // app => do not show resolver

        if (!request.target) {
            return request.handlers
                ? request.handlers.length > 1
                : intent.handlers.length > 1;
        }

        if (request.target === "reuse") {
            return request.handlers
                ? request.handlers.filter((handler) => handler.type === "instance" && handler.instanceId).length > 1 || request.handlers.filter((handler) => handler.type === "app").length > 1
                : intent.handlers.filter((handler) => handler.type === "instance" && handler.instanceId).length > 1 || intent.handlers.filter((handler) => handler.type === "app").length > 1;
        }

        if (request.target === "startNew") {
            return request.handlers
                ? request.handlers.filter((handler) => handler.type === "app").length > 1
                : intent.handlers.filter((handler) => handler.type === "app").length > 1;
        }

        if (typeof request.target === "object") {
            return false;
        }

        return false;
    }

    private buildInteropMethodName(intentName: string): string {
        return `${GLUE42_FDC3_INTENTS_METHOD_PREFIX}${intentName}`;
    }

    private clearUnregistrationPromise(promiseToRemove: Promise<void>): void {
        this.unregisterIntentPromises = this.unregisterIntentPromises.filter((promise) => promise !== promiseToRemove);
    }

    private unsubscribeIntent(intentName: string): void {
        this.myIntents.delete(intentName);

        const methodName = this.buildInteropMethodName(intentName);

        // typings are wrong and mark unregister as a sync method
        const unregisterPromise = this.interop.unregister(methodName) as unknown as Promise<void>;

        this.unregisterIntentPromises.push(unregisterPromise);

        unregisterPromise
            .then(() => {
                this.clearUnregistrationPromise(unregisterPromise);
            })
            .catch((err) => {
                this.logger.error(`Unregistration of a method with name ${methodName} failed with reason: `, err);

                this.clearUnregistrationPromise(unregisterPromise);
            });
    }

    private validateIntentRequest(request: Glue42.Intents.IntentRequest): void {
        this.validateIntentRequestContext(request.context);

        this.validateIntentRequestTarget(request.target);

        this.validateIntentRequestTimeout(request.timeout);

        this.validateWaitUserResponseIndefinitely(request.waitUserResponseIndefinitely);

        if (request.handlers) {
            request.handlers.forEach((handler) => this.validateIntentRequestHandler(handler));
        }
    }

    private validateIntentRequestTarget(target?: "startNew" | "reuse" | { app?: string; instance?: string }): void {
        if (!target) {
            return;
        }

        if (typeof target !== "string" && typeof target !== "object") {
            throw new Error(`Please provide the intent target as one of the valid values: "reuse", "startNew", { app: string }, { instance: string } `);
        }
    }

    private validateIntentRequestContext(context?: any): void {
        if (!context) {
            return;
        }

        if (typeof context !== "object") {
            throw new Error(`Please provide the intent context as an object`);
        }

        if (context.type && typeof context.type !== "string") {
            throw new Error(`Please provide the intent context as an object with 'type' property as string`);
        }

        if (context.data && typeof context.data !== "object") {
            throw new Error(`Please provide the intent context as an object with 'data' property as object`);
        }
    }

    private validateIntentRequestHandler(handler: Glue42.Intents.IntentHandler): void {
        if (!handler.applicationName) {
            throw new Error(`Please provide applicationName for handler ${JSON.stringify(handler)}`);
        }

        if (!handler.type) {
            throw new Error(`Please provide type for handler ${JSON.stringify(handler)}`);
        }

        if (handler.type === "instance" && !handler.instanceId) {
            throw new Error(`Please provide instanceId for handler ${JSON.stringify(handler)}`);
        }
    }

    private validateIntentRequestTimeout(timeout?: number) {
        if (!timeout) {
            return;
        }

        if (typeof timeout !== "number") {
            throw new Error(`Please provide the timeout as a number`);
        }

        if (timeout <= 0) {
            throw new Error(`Please provide the timeout as a positive number`);
        }
    }

    private validateWaitUserResponseIndefinitely(waitUserResponseIndefinitely?: boolean) {
        if (!waitUserResponseIndefinitely) {
            return;
        }

        if (typeof waitUserResponseIndefinitely !== "boolean") {
            throw new Error("Please provide waitUserResponseIndefinitely as a boolean");
        }
    }

    private findHandlerByFilter(handlers: Glue42.Intents.IntentHandler[], filter: { app?: string, instance?: string, type?: "app" | "instance" }) {
        if (filter.type) {
            return handlers.find((handler) => handler.type === filter.type);
        }

        if (filter.instance) {
            return handlers.find((handler) => filter.app
                ? handler.applicationName === filter.app && handler.instanceId === filter.instance
                : handler.instanceId === filter.instance
            );
        }

        if (filter.app) {
            return handlers.find((handler) => handler.applicationName === filter.app);
        }
    }
}
